import { FormattedMessage } from 'react-intl'
import clsx from 'classnames'

import type {
  WidthRange,
  CollectionType,
  StructureType,
  CatalogProductType,
} from '~/presets/catalogPresets'
import styles from './CatalogFilters.module.css'

export interface CatalogFilterState {
  widthRanges: WidthRange[]
  collections: CollectionType[]
  structureTypes: StructureType[]
}

export const INITIAL_FILTERS: CatalogFilterState = {
  widthRanges: [],
  collections: [],
  structureTypes: [],
}

const WIDTH_OPTIONS: {
  value: WidthRange
  labelId: string
  fallback: string
}[] = [
  { value: '0-99', labelId: 'catalog.filter.width.0-99', fallback: '0–99 cm' },
  {
    value: '100-159',
    labelId: 'catalog.filter.width.100-159',
    fallback: '100–159 cm',
  },
  { value: '160+', labelId: 'catalog.filter.width.160+', fallback: '160+ cm' },
]

const COLLECTION_OPTIONS: {
  value: CollectionType
  labelId: string
  fallback: string
}[] = [
  {
    value: 'standard',
    labelId: 'catalog.filter.collection.standard',
    fallback: 'Bazică',
  },
  {
    value: 'new-collection',
    labelId: 'catalog.filter.collection.new',
    fallback: 'Colecție nouă',
  },
]

const STRUCTURE_OPTIONS: {
  value: StructureType
  labelId: string
  fallback: string
}[] = [
  {
    value: 'closed',
    labelId: 'catalog.filter.structure.closed',
    fallback: 'Închis',
  },
  {
    value: 'semi-open',
    labelId: 'catalog.filter.structure.semi',
    fallback: 'Semi-deschis',
  },
]

const COLLECTION_TYPE_TYPES = new Set<CatalogProductType>(['wardrobe'])
const STRUCTURE_TYPE_TYPES = new Set<CatalogProductType>(['wardrobe', 'rack'])
const WIDTH_RANGE_TYPES = new Set<CatalogProductType>([
  'wardrobe',
  'stand',
  'rack',
])

interface Props {
  filters: CatalogFilterState
  onChange: (filters: CatalogFilterState) => void
  selectedType: CatalogProductType
}

function toggleInArray<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]
}

export const CatalogFilters: React.FC<Props> = ({
  filters,
  onChange,
  selectedType,
}) => {
  const showStructure = STRUCTURE_TYPE_TYPES.has(selectedType)
  const showCollection = COLLECTION_TYPE_TYPES.has(selectedType)
  const showWidthRange = WIDTH_RANGE_TYPES.has(selectedType)

  return (
    <div className={styles.filtersContainer}>
      {showWidthRange && (
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>
            <FormattedMessage
              id="catalog.filter.width"
              defaultMessage="Lățime"
            />
          </span>
          <div className={styles.chips}>
            {WIDTH_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={clsx(
                  styles.chip,
                  filters.widthRanges.includes(opt.value) && styles.chipActive
                )}
                onClick={() =>
                  onChange({
                    ...filters,
                    widthRanges: toggleInArray(filters.widthRanges, opt.value),
                  })
                }
              >
                <FormattedMessage
                  id={opt.labelId}
                  defaultMessage={opt.fallback}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {showCollection && (
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>
            <FormattedMessage
              id="catalog.filter.collection"
              defaultMessage="Colecție"
            />
          </span>
          <div className={styles.chips}>
            {COLLECTION_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={clsx(
                  styles.chip,
                  filters.collections.includes(opt.value) && styles.chipActive
                )}
                onClick={() =>
                  onChange({
                    ...filters,
                    collections: toggleInArray(filters.collections, opt.value),
                  })
                }
              >
                <FormattedMessage
                  id={opt.labelId}
                  defaultMessage={opt.fallback}
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {showStructure && (
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>
            <FormattedMessage
              id="catalog.filter.structure"
              defaultMessage="Tip"
            />
          </span>
          <div className={styles.chips}>
            {STRUCTURE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={clsx(
                  styles.chip,
                  filters.structureTypes.includes(opt.value) &&
                    styles.chipActive
                )}
                onClick={() =>
                  onChange({
                    ...filters,
                    structureTypes: toggleInArray(
                      filters.structureTypes,
                      opt.value
                    ),
                  })
                }
              >
                <FormattedMessage
                  id={opt.labelId}
                  defaultMessage={opt.fallback}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
